<template>
  <div class="product-recalls-page">
    <fn-section
      constrained="l"
      padding-top="xs"
      padding-bottom="xs"
    >
      <fn-breadcrumbs :nodes="page.content.breadcrumbs" />
    </fn-section>

    <fn-section
      constrained="l"
      padding-top="xs"
      padding-bottom="xs"
    >
      <div
        v-for="(product, index) of productRecalls"
        :key="product.article_number"
        class="prp-entry"
      >
        <span>{{ formatDateStringToDDMMYYYY(new Date(product.date).toString()) }}</span>

        <div class="e-title">
          <fn-icon
            icon="circle-i-light"
            token-color="color-action-icons-error-default-base"
            size="s"
          />
          {{ $t('productRecalls.title') }}
        </div>

        <span class="e-product-name">{{ product.title }}</span>

        <div class="e-details">
          <div class="d-column">
            <span class="c-heading">{{ $t('productRecalls.articleNumber') }}</span>
            <span>{{ product.article_number }}</span>
          </div>
          <div
            v-if="product.batch_number"
            class="d-column"
          >
            <span class="c-heading">{{ $t('productRecalls.batchNumber') }}</span>
            <span>{{ product.batch_number }}</span>
          </div>
          <div
            v-if="product.best_before_date"
            class="d-column"
          >
            <span class="c-heading">{{ $t('productRecalls.bestBeforeDate') }}</span>
            <span>{{ formatDateStringToDDMMYYYY(new Date(product.best_before_date).toString()) }}</span>
          </div>
        </div>

        <img
          class="e-image"
          :fetchpriority="index === 0 ? 'high' : 'auto'"
          :loading="index === 0 ? 'eager' : 'lazy'"
          :src="product.image.url"
          :alt="product.image.alt"
        />

        <p
          class="e-additional-info"
          v-html="convertLineBreaksToHtml(product.text)"
        />

        <div class="e-contact">
          <span>{{ $t('productRecalls.contact') }}</span>
          <span v-html="convertLineBreaksToHtml(product.contact)" />
        </div>
      </div>
    </fn-section>
  </div>
</template>

<script setup lang="ts">
const { getProductRecalls: productRecalls } = storeToRefs(useMainStore());

defineProps<{
  page: Record<string, any>;
}>();
</script>

<style lang="scss" scoped>
.product-recalls-page {
  .prp-entry {
    padding: 2 * $pad 0;
    display: flex;
    flex-direction: column;
    gap: $pad;
    font-size: map-get($font-sizes, lxl);
    font-weight: map-get($font-weights, normal);
    border-bottom: 1px solid $color-general-border-neutral-default;
    color: $color-action-text-neutral-default-base;

    &:first-child {
      padding-top: 0;
    }

    &:last-child {
      border: none;
    }

    .e-title {
      display: flex;
      gap: math.div($pad, 2);
      font-weight: map-get($font-weights, bold);
      align-items: center;
    }

    .e-product-name {
      font-size: map-get($font-sizes-headings, m);
      font-weight: map-get($font-weights, medium-bold);
    }

    .e-details {
      display: flex;
      gap: 2 * $pad;

      @include bp(m, max) {
        gap: $pad;
        flex-direction: column;
      }

      .d-column {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;

        .c-heading {
          font-weight: map-get($font-weights, bold);
        }
      }
    }

    .e-image {
      margin: $pad 0;
      max-width: 240px;
      max-height: 240px;
      object-fit: contain;
    }

    .e-additional-info {
      font-size: map-get($font-sizes, lxl);
    }

    .e-contact {
      display: flex;
      flex-direction: column;

      span:first-child {
        font-weight: map-get($font-weights, bold);
      }
    }
  }

  .breadcrumbs {
    border-bottom: none;
  }
}
</style>
